import React, { useCallback } from 'react';
import { Button, Pane, Text, Toggle } from '@leverege/ui-elements';
import StringUtil from '@leverege/string-util';
import I18N from '@leverege/i18n';
import { DailyHours, HoursRange } from '@leverege/operating-hours';
import { useValueChange } from '@leverege/ui-hooks';
import { useResizeDetector } from 'react-resize-detector';
import CopyHoursWidget from "./CopyHoursWidget.js";
import RangeEditor from "./RangeEditor.js";
import { getEvenOddVariant } from "./Util.js";
import S from './WeeklyHoursEditor.css';
const {
  t
} = I18N.ns('imagine.operatingHours.DailyHoursEditor');
export default function DailyHoursEditor(props) {
  const {
    value,
    index,
    onChange: pOnChange,
    eventData,
    blueprint
  } = props;
  const {
    width,
    ref
  } = useResizeDetector();
  const onChange = useValueChange(DailyHours, props);
  const ranges = DailyHours.getRanges(value);
  const isEven = index % 2 === 0;
  const onDeleteRange = ({
    data
  }) => {
    const ranges = DailyHours.getRanges(value);
    if (ranges.length > 1) {
      const nValue = DailyHours.removeRangeAt(value, data);
      pOnChange({
        value: nValue,
        data: eventData
      });
    } else {
      const nValue = DailyHours.setOpen(value, false);
      pOnChange({
        value: nValue,
        data: eventData
      });
    }
  };
  const open = DailyHours.getOpen(value);
  const onAddRange = useCallback(() => {
    if (open) {
      const nValue = DailyHours.addRange(value, HoursRange.create());
      pOnChange({
        value: nValue,
        data: eventData
      });
    } else {
      const nValue = DailyHours.setOpen(value, true);
      pOnChange({
        value: nValue,
        data: eventData
      });
    }
  }, [value, eventData, pOnChange, open]);
  const onCopyHours = useCallback(({
    value: val
  }) => {
    const update = {
      data: {},
      value: {}
    };
    let hasUpdate = false;
    Object.entries(val).forEach(([key, v]) => {
      if (v === true) {
        hasUpdate = true;
        const dayName = StringUtil.capitalize(key);
        const setName = `set${dayName}`;
        update.data[setName] = key;
        update.value[key] = {
          ...value,
          name: dayName
        };
      }
    });
    if (hasUpdate) {
      pOnChange(update);
    }
  }, [value, pOnChange]);
  const name = width > 500 ? DailyHours.getName(value) : DailyHours.getName(value).substring(0, 3);
  return /*#__PURE__*/React.createElement(Pane, {
    variant: getEvenOddVariant('operatingHoursDay', isEven),
    className: S.grid,
    domRef: ref
  }, /*#__PURE__*/React.createElement(Text, {
    variant: getEvenOddVariant('operatingHoursDay', isEven, 'bodySmallBold'),
    className: S.gridFirstColumn
  }, name), /*#__PURE__*/React.createElement(Toggle, {
    variant: getEvenOddVariant('operatingHours', isEven),
    eventData: "setOpen",
    onChange: onChange,
    value: open
  }), !open ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    className: S.closed,
    variant: getEvenOddVariant('operatingHoursClosed', isEven)
  }, t('businessClosed', {
    name: blueprint?.name || 'Location'
  })), /*#__PURE__*/React.createElement(Pane, {
    variant: getEvenOddVariant('operatingHoursDayToolbar', isEven),
    className: S.toolbar
  }, /*#__PURE__*/React.createElement(Button, {
    className: S.add,
    onClick: onAddRange,
    icon: t('addIcon'),
    variant: getEvenOddVariant('dailyHoursAction', isEven, 'linkNeutralNoPad')
  }), /*#__PURE__*/React.createElement(CopyHoursWidget, {
    isEven: isEven,
    className: S.copy,
    onChange: onCopyHours,
    eventData: eventData
  }))) : ranges.map((range, index) => {
    const start = HoursRange.getStart(range);
    const end = HoursRange.getEnd(range);
    return /*#__PURE__*/React.createElement(RangeEditor, {
      key: `${start}-${end}-${index}`,
      index: index,
      isEven: isEven,
      range: range,
      onChange: pOnChange,
      value: value,
      eventData: eventData,
      onDeleteRange: onDeleteRange,
      onCopyHours: onCopyHours,
      onAddRange: onAddRange
    });
  }));
}